import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import { Package, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { cn } from '../utils/cn';

const CATEGORIES = ['Windows', 'Office', 'Visio', 'Project', 'Antivirus', 'Other'];
const ACTIVATION_TYPES = ['Retail', 'OEM', 'LTSC', 'Subscription'];

export default function Products() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ shop_id: '', name: '', sku: '', category: 'Other', activation_type: 'Retail', description: '' });

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, shopsRes] = await Promise.all([api.get('/products'), api.get('/shops')]);
      setProducts(productsRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ shop_id: shops[0]?.id || '', name: '', sku: '', category: 'Other', activation_type: 'Retail', description: '' });
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({ shop_id: product.shop_id, name: product.name, sku: product.sku, category: product.category, activation_type: product.activation_type, description: product.description || '' });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/products/${editing.id}`, form); } else { await api.post('/products', form); }
      setModalOpen(false);
      fetchData();
    } catch (error) { console.error('Save failed:', error); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('products.delete_confirm'))) return;
    try { await api.delete(`/products/${id}`); fetchData(); } catch (error) { console.error('Delete failed:', error); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('products.title')}</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" /> {t('products.add')}
        </button>
      </div>

      {products.length === 0 ? (
        <EmptyState icon={Package} title={t('products.no_results')} description={t('products.no_results_hint')} action={<button onClick={openCreate} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('products.add')}</button>} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.product')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.sku')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.category')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.stock')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.shop')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{product.sku}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.activation_type}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={cn('text-sm font-semibold', product.stock === 0 ? 'text-red-600' : product.stock <= 5 ? 'text-orange-600' : 'text-green-600')}>
                      {product.stock === 0 && <AlertTriangle className="inline h-4 w-4 mr-1" />}
                      {product.stock}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.shop?.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button onClick={() => openEdit(product)} className="text-gray-400 hover:text-primary-600 mr-2"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => handleDelete(product.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('products.edit_title') : t('products.create_title')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.shop')} *</label><select value={form.shop_id} onChange={(e) => setForm({ ...form, shop_id: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.name')} *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.sku')} *</label><input type="text" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.category')}</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.activation_type')}</label><select value={form.activation_type} onChange={(e) => setForm({ ...form, activation_type: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">{ACTIVATION_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</select></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('products.description')}</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{editing ? t('common.update') : t('common.create')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
